import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Truck, Info, Warning } from "@phosphor-icons/react";
import { CARRIERS } from "@/lib/carriers";

const SHIPPING_TYPES = ["Local", "Interstate", "International"];

function itemLabel(it) {
  return it.order_type === "other"
    ? it.item_name
    : [it.manufacturer_name, it.series_name, it.product_name].filter(Boolean).join(" · ");
}

export default function MarkOrderedDialog({ items, item, open, onOpenChange, onSuccess }) {
  const [supplier, setSupplier] = useState("");
  const [shipping, setShipping] = useState("");
  const [eta, setEta] = useState("");
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setSupplier(""); setShipping(""); setEta(""); setCarrier(""); setTracking(""); }
  }, [open]);

  // Accept either a single `item` (legacy) or an `items` array.
  const list = (Array.isArray(items) ? items : []).concat(item ? [item] : []);
  if (list.length === 0) return null;

  const single = list.length === 1 ? list[0] : null;
  const eligible = list.filter((i) => (i.order_type === "film" || i.order_type === "tools") && i.product_id);
  const canPo = eligible.length > 0;
  const isCatalogueAny = list.some((i) => i.order_type === "film" || i.order_type === "tools");
  const label = single ? (itemLabel(single) || "—") : `${list.length} items`;

  const submit = async (create_po) => {
    setSaving(true);
    try {
      const { data } = await api.post("/order-items/mark-ordered-bulk", {
        ids: list.map((i) => i.id),
        create_po,
        supplier,
        shipping_type: shipping,
        eta: eta || null,
        carrier: create_po ? carrier : "",
        tracking_number: create_po ? tracking : "",
      });
      toast.success(
        data.po_id
          ? `Ordered — Purchase Order created (${data.po_items} item${data.po_items === 1 ? "" : "s"}) in Incoming Stock`
          : `Marked ${data.ordered} item${data.ordered === 1 ? "" : "s"} as ordered`
      );
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mark-ordered-dialog">
        <DialogHeader>
          <DialogTitle>{isCatalogueAny ? "Move to Purchase Orders?" : "Mark as ordered?"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
            {single ? (
              <><span className="text-slate-500">Item:</span> <span className="font-medium">{label}</span></>
            ) : (
              <>
                <div className="text-slate-500 mb-1.5">Marking <span className="font-medium text-slate-800">{list.length} items</span> ordered together:</div>
                <ul className="list-disc pl-5 space-y-0.5 max-h-32 overflow-y-auto">
                  {list.map((it) => <li key={it.id} className="text-slate-700">{itemLabel(it) || "—"}</li>)}
                </ul>
              </>
            )}
          </div>

          {isCatalogueAny ? (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 flex gap-2">
              <Info size={18} weight="fill" className="shrink-0 mt-0.5" />
              <div>Creating a Purchase Order files {single ? "this item" : "the film/tools items"} into <strong>Incoming Stock</strong> as one shipment ("In Transit"). When it arrives you receive it there and it becomes live stock. You can also just mark {single ? "it" : "them"} ordered to keep a record here.</div>
            </div>
          ) : (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600 flex gap-2">
              <Info size={18} weight="fill" className="shrink-0 mt-0.5" />
              <div>"Other" items can't be tracked as Purchase Orders. This will move {single ? "it" : "them"} into the dated <strong>Ordered</strong> list as a record.</div>
            </div>
          )}

          {isCatalogueAny && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Supplier (optional)</Label>
                <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="e.g. 3M Direct" data-testid="mark-supplier" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Courier</Label>
                <Select value={carrier} onValueChange={setCarrier}>
                  <SelectTrigger data-testid="mark-carrier"><SelectValue placeholder="Select courier…" /></SelectTrigger>
                  <SelectContent>
                    {CARRIERS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">ETA</Label>
                <Input type="date" value={eta} onChange={(e) => setEta(e.target.value)} data-testid="mark-eta" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Tracking number</Label>
                <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="e.g. CP1234567890" className="font-mono" data-testid="mark-tracking" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Shipping</Label>
                <Select value={shipping} onValueChange={setShipping}>
                  <SelectTrigger data-testid="mark-shipping"><SelectValue placeholder="Select shipping…" /></SelectTrigger>
                  <SelectContent>
                    {SHIPPING_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {!canPo && (
                <div className="col-span-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800 flex gap-2">
                  <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
                  Brand, Series and Quantity must all be set before an item can become a Purchase Order.
                </div>
              )}
              {canPo && !single && eligible.length < list.length && (
                <div className="col-span-2 text-xs text-slate-500">
                  {eligible.length} of {list.length} selected will go into the Purchase Order; the rest are just marked ordered.
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-md">Cancel</Button>
          <Button variant="outline" onClick={() => submit(false)} disabled={saving} className="rounded-md" data-testid="just-mark-ordered">
            Just mark ordered
          </Button>
          {isCatalogueAny && (
            <Button onClick={() => submit(true)} disabled={saving || !canPo} className="bg-blue-600 hover:bg-blue-700 rounded-md gap-2" data-testid="create-po-btn">
              <Truck size={16} /> Create PO &amp; order
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
